'use client';

import { useEffect } from 'react';
import { useUiPreferences } from '@/app/Components/UiPreferencesProvider';
import { SEMESTER_OPTIONS, type SemesterId } from '@/app/lib/semesters';

type DiscoveryParams = { get: (key: string) => string | null; toString: () => string };
const supportedSemester = (value: string | null): value is SemesterId => SEMESTER_OPTIONS.some(({ id }) => id === value);

/** Public URL context wins; account schedules and degree catalog years remain independent. */
export function useDiscoverySemester(params: DiscoveryParams, pathname: string) {
  const { activeSemester, setActiveSemester, explorerPreferencesHydrated } = useUiPreferences();
  const requestedSemester = params.get('semester');
  const semester = supportedSemester(requestedSemester) ? requestedSemester : activeSemester;

  useEffect(() => {
    // The provider restores the device's semester in the same hydration pass as
    // Explorer preferences. Pin only afterwards so Back/reload retain that term.
    if (!explorerPreferencesHydrated || supportedSemester(requestedSemester)) return;
    const next = new URLSearchParams(window.location.search);
    if (supportedSemester(next.get('semester'))) return;
    next.set('semester', activeSemester);
    window.history.replaceState(null, '', `${pathname}?${next}`);
  }, [activeSemester, explorerPreferencesHydrated, pathname, requestedSemester]);

  const selectSemester = (value: string) => {
    if (!supportedSemester(value) || value === semester) return;
    const next = new URLSearchParams(window.location.search);
    next.set('semester', value);
    next.delete('course');
    next.delete('page');
    window.history.pushState(null, '', `${pathname}?${next}`);
    setActiveSemester(value);
  };

  return { semester, selectSemester };
}
